import { SupabaseClient } from "@supabase/supabase-js";
import { BASE_URL, COUNTRY_CODE } from "@utils/constants";
import axios, { AxiosError } from "axios";
import { analytics } from "@components/analytics";
import { authorizedFetcher } from "@utils/fetchers";
import { getCookieValue } from "@utils/helpers";
import { mutate } from "swr";
import { SyntheticEvent } from "react";

export async function patchPostCode(supabase: SupabaseClient) {
  const websiteUid = getCookieValue("itk-website-uid");
  const content_language = getCookieValue("NEXT_LOCALE");
  const { data: sessionData, error: sessionError } =
    await supabase.auth.getSession();

  if (!sessionData.session || sessionError) return;

  const user = await authorizedFetcher([
    "account",
    sessionData.session.access_token,
  ]);

  if (user?.postcode) {
    const basketKey = getCookieValue("ASP.NET_SessionId");
    try {
      await axios.patch(
        `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}/checkout/postcode`,
        {
          postCode: user?.postcode,
        },
        {
          headers: {
            "content-language": content_language,
            "itk-website-uid": websiteUid,
          },
        }
      );
      await mutate(`basket/${basketKey}/checkout`);
      await mutate(
        (key: any) =>
          Array.isArray(key) && key[0] === `basket/${basketKey}/checkout`
      );
      return "success";
    } catch (err: AxiosError | any) {
      if (err.response && err.response.status >= 400 && err.status < 500) {
        return { status: "failure", message: err.response.data.message };
      } else {
        console.error(err);

        return {
          status: "failure",
          message: err.response.data.message ?? "Something went wrong",
        };
      }
    }
  }
}

export async function handleOTPUser(
  event: SyntheticEvent<HTMLFormElement>,
  supabase: SupabaseClient,
  signUpHref?: string,
  isCheckout?: boolean
) {
  try {
    const { email } = Object.fromEntries(new FormData(event.currentTarget));
    const { error } = await supabase.auth.signInWithOtp({
      email: email as string,
      options: {
        shouldCreateUser: false,
      },
    });

    if (error) {
      if (error.status === 422) {
        const response = await axios.post(`/api/otp-new-user`, {
          email: email as string,
          redirectTo: signUpHref,
          isCheckout,
        });

        if (response.status === 200) {
          return {
            status: "success",
            message: "Email sent",
          };
        } else {
          throw new Error(`Failed to send email: ${response.statusText}`);
        }
      }
      throw error;
    }
  } catch {
    return {
      status: "failure",
      message: "Something went wrong",
    };
  }
}

export async function handleSignInUser(
  event: SyntheticEvent<HTMLFormElement>,
  supabase: SupabaseClient
) {
  const { email, password } = Object.fromEntries(
    new FormData(event.currentTarget)
  );

  const { error, data } = await supabase.auth.signInWithPassword({
    email: email as string,
    password: password as string,
  });

  if (error) {
    return { status: "failure", message: error.message };
  }

  if (!error && !data.user.app_metadata["iticket_user_uid"]) {
    await supabase.auth.signOut();
    analytics.reset();

    return { status: "failure", message: "Invalid login details" };
  }

  if (data.user) {
    analytics.identify(
      data?.user?.app_metadata?.iticket_user_uid
        ? data.user.app_metadata.iticket_user_uid.toLowerCase()
        : (email as string),
      {
        id: data?.user?.app_metadata?.iticket_user_uid?.toLowerCase(),
        email: email as string,
      }
    );

    analytics.track("Sign in", {
      iticket_uid: data?.user?.app_metadata?.iticket_user_uid?.toLowerCase(),
      email: email,
      method: "password",
    });
  }

  return {
    status: "success",
    message: "Logged In",
  };
}
